import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import OnboardingTour from '@/components/OnboardingTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calendarAPI, authAPI, videoAPI } from '@/utils/api';
import DateTimePicker from '@/components/DateTimePicker';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Phone, Video, AlertCircle } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import ClinicalScoreWidget from '@/components/ClinicalScoreWidget';

const Dashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [coParentWaiting, setCoParentWaiting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    memo: '',
    start_time: '',
    end_time: '',
    custody_parent: 'parent1',
    color: '#4f46e5',
  });

  const checkCoParentWaiting = async () => {
    try {
      const response = await videoAPI.checkCoParentWaiting();
      if (response.data.waiting) {
        setCoParentWaiting(response.data.call);
      } else {
        setCoParentWaiting(null);
      }
    } catch (error) {
      console.error('Error checking co-parent waiting status:', error);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchEvents();
    checkCoParentWaiting();
    
    // Poll every 10 seconds for co-parent waiting status
    const interval = setInterval(checkCoParentWaiting, 10000);
    
    return () => clearInterval(interval);
  }, [currentDate]);

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await calendarAPI.getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load events';
      toast.error(errorMsg, {
        description: 'Please try refreshing the page or contact support if the issue persists.',
        action: {
          label: 'Retry',
          onClick: () => fetchEvents()
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.start_time || !newEvent.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await calendarAPI.createEvent({
        ...newEvent,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString(),
      });
      toast.success('Event created successfully!');
      setShowAddDialog(false);
      setNewEvent({
        title: '',
        description: '',
        memo: '',
        start_time: '',
        end_time: '',
        custody_parent: 'parent1',
        color: '#4f46e5',
      });
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Add padding days for calendar grid
  const startDayOfWeek = monthStart.getDay();
  const paddingDays = Array(startDayOfWeek).fill(null);

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventStart = new Date(event.start_time || event.start);
      const eventEnd = new Date(event.end_time || event.end);
      return day >= eventStart.setHours(0,0,0,0) && day <= eventEnd.setHours(23,59,59,999);
    });
  };

  const custodyColors = {
    parent1: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
    parent2: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
    shared: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout setIsAuthenticated={setIsAuthenticated}>
      <div className="space-y-6 animate-fade-in">
        {/* Header with Quick Actions */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Custody Calendar</h1>
            <p className="text-gray-600 mt-1">Your shared parenting schedule</p>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              className="flex items-center space-x-2"
              onClick={() => navigate('/video-call')}
            >
              <Phone className="w-4 h-4" />
              <span>Call</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex items-center space-x-2"
              onClick={() => navigate('/video-call')}
            >
              <Video className="w-4 h-4" />
              <span>Video</span>
            </Button>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-event-button" className="flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Event</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Event</DialogTitle>
                  <DialogDescription>Add event to shared custody calendar</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Event Title *</Label>
                    <Input
                      id="title"
                      data-testid="event-title"
                      placeholder="e.g., Parent 1 Custody, School Event"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custody_parent">Custody Assignment</Label>
                    <Select value={newEvent.custody_parent} onValueChange={(value) => {
                      const colors = { parent1: '#4f46e5', parent2: '#9333ea', shared: '#10b981' };
                      setNewEvent({ ...newEvent, custody_parent: value, color: colors[value] });
                    }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parent1">Parent 1 Custody</SelectItem>
                        <SelectItem value="parent2">Parent 2 Custody</SelectItem>
                        <SelectItem value="shared">Shared Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Event details"
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="memo">Memo (Visible to both parents)</Label>
                    <Textarea
                      id="memo"
                      placeholder="Notes, reminders, or important information"
                      value={newEvent.memo}
                      onChange={(e) => setNewEvent({ ...newEvent, memo: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <DateTimePicker
                      id="start_time"
                      label="Start Date & Time"
                      value={newEvent.start_time}
                      onChange={(value) => setNewEvent({ ...newEvent, start_time: value })}
                      required
                    />
                    <DateTimePicker
                      id="end_time"
                      label="End Date & Time"
                      value={newEvent.end_time}
                      onChange={(value) => setNewEvent({ ...newEvent, end_time: value })}
                      required
                    />
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                    <p className="font-semibold">Court-Admissible Record</p>
                    <p className="text-xs text-blue-700 mt-1">This event will be timestamped and logged for legal purposes. All changes are tracked.</p>
                  </div>
                  <Button onClick={handleAddEvent} className="w-full">
                    Create Event
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Co-Parent Calling Notification */}
        {coParentWaiting && (
          <Alert className="mb-6 bg-green-50 border-green-500 animate-pulse">
            <Video className="h-5 w-5 text-green-600" />
            <AlertTitle className="text-green-900 font-bold">
              📞 {coParentWaiting.caller_name} is calling you!
            </AlertTitle>
            <AlertDescription className="text-green-800">
              <p className="mb-3">
                Your co-parent is waiting in a video call room: <strong>{coParentWaiting.room_name}</strong>
              </p>
              <Button
                onClick={() => navigate(coParentWaiting.call_link.replace(window.location.origin, ''))}
                className="bg-green-600 hover:bg-green-700"
              >
                <Video className="w-4 h-4 mr-2" />
                Join Call Now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Grid - Calendar and Score */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Column (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Legend */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span className="text-sm">Parent 1 Custody</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-purple-500 rounded"></div>
                    <span className="text-sm">Parent 2 Custody</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm">Shared Time</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calendar */}
            <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-2xl">
                {format(currentDate, 'MMMM yyyy')}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Week days header */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 text-sm py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-2">
              {paddingDays.map((_, index) => (
                <div key={`padding-${index}`} className="aspect-square" />
              ))}
              {daysInMonth.map(day => {
                const dayEvents = getEventsForDay(day);
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div
                    key={day.toString()}
                    className={`aspect-square border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md ${
                      isToday ? 'border-primary border-2 bg-primary/5' : 'border-gray-200'
                    }`}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className={`text-sm font-semibold mb-1 ${
                      isToday ? 'text-primary' : 'text-gray-700'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map(event => (
                        <div
                          key={event.id}
                          className="text-xs px-1 py-0.5 rounded truncate"
                          style={{ backgroundColor: event.color + '40', color: event.color }}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        {selectedDate && (
          <Card>
            <CardHeader>
              <CardTitle>Events for {format(selectedDate, 'MMMM d, yyyy')}</CardTitle>
            </CardHeader>
            <CardContent>
              {getEventsForDay(selectedDate).length > 0 ? (
                <div className="space-y-3">
                  {getEventsForDay(selectedDate).map(event => (
                    <div key={event.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{event.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {format(new Date(event.start_time || event.start), 'h:mm a')} - {format(new Date(event.end_time || event.end), 'h:mm a')}
                          </p>
                          {event.description && (
                            <p className="text-sm text-gray-700 mt-2">{event.description}</p>
                          )}
                          {event.memo && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs font-semibold text-yellow-900">Memo:</p>
                              <p className="text-sm text-yellow-800 mt-1">{event.memo}</p>
                            </div>
                          )}
                          <div className="mt-3 text-xs text-gray-500">
                            <p>Created: {format(new Date(event.created_at), 'MMM dd, yyyy \u2022 h:mm a')}</p>
                            <p className="mt-1">Last modified: {format(new Date(event.updated_at), 'MMM dd, yyyy \u2022 h:mm a')}</p>
                          </div>
                        </div>
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: event.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No events for this day</p>
              )}
            </CardContent>
          </Card>
        )}
          </div>

          {/* Score Column (1/3 width) */}
          <div className="lg:col-span-1">
            <ClinicalScoreWidget />
          </div>
        </div>
      </div>
      <OnboardingTour />
    </Layout>
  );
};

export default Dashboard;
